/**
 * configuracoes.service.js — Busca as configurações da loja via Edge Function pública.
 * Estratégia: stale-while-revalidate com sessionStorage (TTL: 10 min).
 * - Se há cache válido: retorna imediatamente + revalida em background.
 * - Se cache expirado ou ausente: aguarda o fetch (comportamento original).
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const ANON_KEY    = import.meta.env.VITE_SUPABASE_ANON_KEY;

const CACHE_KEY = 'voxcar_config_cache';
const CACHE_TTL = 10 * 60 * 1000; // 10 minutos em ms

// ── Cache helpers ─────────────────────────────────────────────────

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) return null; // expirado
    return data;
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    // localStorage indisponível (modo privado restrito) — ignora silenciosamente
  }
}

// ── Fetch da API ──────────────────────────────────────────────────

async function fetchFromApi() {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/get-configuracoes`, {
    headers: {
      'apikey': ANON_KEY,
      'Authorization': `Bearer ${ANON_KEY}`,
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  return json.configuracoes ?? null;
}

// ── Ponto de entrada ──────────────────────────────────────────────

/**
 * Retorna as configurações da loja com cache stale-while-revalidate.
 * @returns {Promise<{data: object|null, error: string|null}>}
 */
export async function fetchConfiguracoes() {
  const cached = readCache();

  if (cached) {
    // Retorna o cache imediatamente (sem flash)
    // e revalida em background para manter os dados frescos
    fetchFromApi()
      .then(fresh => { if (fresh) writeCache(fresh); })
      .catch(() => { /* falha silenciosa — usa o cache */ });

    return { data: cached, error: null };
  }

  // Sem cache: aguarda o fetch normalmente
  try {
    const data = await fetchFromApi();
    if (data) writeCache(data);
    return { data, error: null };
  } catch (err) {
    console.error('[configuracoes.service]', err);
    return { data: null, error: err.message };
  }
}

/**
 * Invalida o cache manualmente (use ao salvar configurações no CRM).
 */
export function invalidarCacheConfiguracoes() {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch { /* ignora */ }
}

